// Matching approved prompts to the runs they were written for.
//
// A "run" is one (ad × variation axis) pair. Its prompt is written from that
// ad's own analyses under that axis, and the axis decides what the build sheet
// redacts and whether the source ad is even attached — so two runs genuinely
// cannot share a prompt. Sending one run's prompt for another's render still
// produces a plausible image, which is exactly why it needs a test rather than
// a reviewer's eye.
import { TextElement } from 'services/StaticAdsService';
import { GenerationSimilarity } from './types';

export interface Run {
  staticAdId: string;
  axis: GenerationSimilarity;
}

export interface PreviewEntry extends Run {
  prompt: string;
  aspect: string | null;
  sheet: Record<string, unknown> | null;
  unfilled: string[];
  needsPhoto: boolean;
  /** Strings the image will render. Empty in overlay mode, and empty in AI mode
   *  only when stage 2 ignored the format — which is worth saying out loud. */
  textElements: TextElement[];
  /** Set when stage 2 failed for this run; the others still previewed. */
  error?: string;
}

export const runKey = (staticAdId: string, axis: GenerationSimilarity) => `${staticAdId}|${axis}`;

/**
 * The runs a submit covers: every ad against every chosen axis.
 *
 * `pending` short-circuits it — a retry after a partial submit covers ONLY what
 * never landed, because re-deriving the whole batch would re-queue, and re-bill,
 * the runs that already succeeded.
 */
export function buildRuns(
  staticAdIds: string[],
  axes: GenerationSimilarity[],
  pending?: Run[] | null,
): Run[] {
  if (pending && pending.length) return pending;
  return staticAdIds.flatMap(id => axes.map(axis => ({ staticAdId: id, axis })));
}

/**
 * The prompt approved for THIS run, or null if it has none.
 *
 * Null is a refusal, not a fallback: a run with no approved prompt must not be
 * queued, because queueing it re-runs stage 2 server-side and renders from a
 * prompt nobody read.
 */
export function approvedPrompt(previews: PreviewEntry[], run: Run): PreviewEntry | null {
  const match = previews.find(e => e.staticAdId === run.staticAdId && e.axis === run.axis);
  if (!match || match.error || !match.prompt.trim()) return null;
  return match;
}

/**
 * Everything a preview was written from.
 *
 * Approving a prompt approves it for one configuration; change the product,
 * the photos, the selling points, the brief, the axes or the text mode and the
 * approved text describes a setup that no longer exists. Comparing this string
 * is what makes the dialog throw those prompts away instead of shipping them.
 */
export function previewConfigKey(config: {
  staticAdIds: string[];
  axes: GenerationSimilarity[];
  productId?: string | number | null;
  assetIds: string[];
  sellingPoints: string[];
  brief: string;
  textMode: string;
}): string {
  return JSON.stringify({
    ads: [...config.staticAdIds].sort(),
    axes: [...config.axes].sort(),
    product: config.productId ?? null,
    assets: [...config.assetIds].sort(),
    points: [...config.sellingPoints].sort(),
    brief: config.brief.trim(),
    textMode: config.textMode,
  });
}
